import encoderUrl from './worklets/pcm-encoder.worklet.ts?worker&url';

export type EncoderHandle = {
  context: AudioContext;
  workletNode: AudioWorkletNode;
  micSource: MediaStreamAudioSourceNode;
  /** Seq-Zähler auf 0 zurücksetzen (Aufnahme-Start) — Backup beginnt dann bei Seq 0. */
  resetSeq(): void;
  stop(): void;
};

export async function startEncoder(opts: {
  deviceId?: string;
  onPacket: (packet: ArrayBuffer) => void;
}): Promise<EncoderHandle> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      deviceId: opts.deviceId ? { exact: opts.deviceId } : undefined,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    },
    video: false,
  });

  // If any setup step after getUserMedia fails (worklet module load, node
  // construction), stop the already-acquired mic so it doesn't stay hot — the
  // tab mic indicator would otherwise stay on, and the connect/retry UI would
  // stack a fresh live stream on every attempt.
  try {
    const context = new AudioContext({ sampleRate: 48000 });
    await context.audioWorklet.addModule(encoderUrl);
    const workletNode = new AudioWorkletNode(context, 'pcm-encoder', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
    });
    workletNode.port.onmessage = (evt) => {
      if (evt.data instanceof ArrayBuffer) opts.onPacket(evt.data);
    };

    const micSource = context.createMediaStreamSource(stream);
    micSource.connect(workletNode);

    // Diagnose (Sample-Rate-Hang, z. B. Loopback-Routing): der L16-Pfad ERZWINGT 48 kHz im AudioContext.
    // Läuft das Aufnahme-Gerät nativ auf einer anderen Rate (z. B. 44,1 kHz), muss der Browser/Treiber
    // resampeln — bei exklusiv gehaltenen virtuellen Geräten kann das den Treiber (und ein parallel
    // laufendes Nuendo) aufhängen. Diese Zeile macht den Mismatch im Speaker-Tab sofort sichtbar.
    try {
      const s = stream.getAudioTracks()[0]?.getSettings() ?? {};
      const devRate = (s as { sampleRate?: number }).sampleRate;
      const tag = devRate && devRate !== context.sampleRate ? 'MISMATCH' : 'ok';
      console.info(`[tro] capture: context=${context.sampleRate}Hz device=${devRate ?? '?'}Hz [${tag}] label="${(s as { label?: string }).label ?? ''}"`);
    } catch { /* getSettings nicht verfügbar — egal */ }

    return {
      context,
      workletNode,
      micSource,
      resetSeq() { workletNode.port.postMessage({ type: 'reset' }); },
      stop() {
        workletNode.disconnect();
        micSource.disconnect();
        stream.getTracks().forEach((t) => t.stop());
        void context.close();
      },
    };
  } catch (err) {
    stream.getTracks().forEach((t) => t.stop());
    throw err;
  }
}
